/**
 * Card Component
 * 
 * @description
 * Reusable card component with multiple variants for iGaming platform.
 * Supports default, neon, and gold accent variants.
 * 
 * @module components/ui/Card
 */

import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

export type CardVariant = 'default' | 'neon' | 'gold';
export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Visual style variant */
  variant?: CardVariant;
  /** Padding size */
  padding?: CardPadding;
  /** Interactive card - has hover effects */
  interactive?: boolean;
}

/**
 * Card Component
 * 
 * @example
 * ```tsx
 * <Card variant="neon" padding="md" interactive>
 *   <h3>Card Title</h3>
 *   <p>Card content here</p>
 * </Card>
 * ```
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      className,
      variant = 'default',
      padding = 'md',
      interactive = false,
      children,
      ...props
    },
    ref
  ) => {
    /**
     * Base styles applied to all cards
     */
    const baseStyles = cn(
      'bg-background-secondary border rounded-lg',
      'transition-shadow duration-200'
    );

    /**
     * Variant-specific border and shadow styles
     */
    const variants = {
      default: cn('border-border shadow-card'),
      neon: cn(
        'border-border',
        'hover:shadow-neon hover:border-neon-cyan/50'
      ),
      gold: cn(
        'border-border',
        'hover:shadow-gold hover:border-gold/50'
      ),
    };

    /**
     * Interactive adds hover scale and shadow transition
     */
    const interactiveStyles = interactive && cn(
      'hover:scale-[1.02]',
      variant === 'neon' ? 'hover:shadow-neon-lg' : 'hover:shadow-card-hover'
    );

    /**
     * Padding sizes
     */
    const paddings = {
      none: '',
      sm: 'p-3',
      md: 'p-4 sm:p-6',
      lg: 'p-6 sm:p-8',
    };

    return (
      <div
        ref={ref}
        className={cn(
          baseStyles,
          variants[variant],
          interactiveStyles,
          paddings[padding],
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

/**
 * Card Header Component
 */
export interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {}

export function CardHeader({
  className,
  children,
  ...props
}: CardHeaderProps): React.ReactElement {
  return (
    <div
      className={cn('flex flex-col gap-1 mb-4', className)}
      {...props}
    >
      {children}
    </div>
  );
}

CardHeader.displayName = 'CardHeader';

/**
 * Card Title Component
 */
export interface CardTitleProps extends HTMLAttributes<HTMLHeadingElement> {}

export function CardTitle({
  className,
  children,
  ...props
}: CardTitleProps): React.ReactElement {
  return (
    <h3
      className={cn('text-lg font-semibold text-foreground font-display', className)}
      {...props}
    >
      {children}
    </h3>
  );
}

CardTitle.displayName = 'CardTitle';

/**
 * Card Description Component
 */
export interface CardDescriptionProps extends HTMLAttributes<HTMLParagraphElement> {}

export function CardDescription({
  className,
  children,
  ...props
}: CardDescriptionProps): React.ReactElement {
  return (
    <p
      className={cn('text-sm text-foreground-muted', className)}
      {...props}
    >
      {children}
    </p>
  );
}

CardDescription.displayName = 'CardDescription';

/**
 * Card Content Component
 */
export interface CardContentProps extends HTMLAttributes<HTMLDivElement> {}

export function CardContent({
  className,
  children,
  ...props
}: CardContentProps): React.ReactElement {
  return (
    <div className={cn('', className)} {...props}>
      {children}
    </div>
  );
}

CardContent.displayName = 'CardContent';

/**
 * Card Footer Component
 */
export interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {}

export function CardFooter({
  className,
  children,
  ...props
}: CardFooterProps): React.ReactElement {
  return (
    <div
      className={cn('flex items-center gap-3 mt-4 pt-4 border-t border-border', className)}
      {...props}
    >
      {children}
    </div>
  );
}

CardFooter.displayName = 'CardFooter';